import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";

const IMPACT_SCOPES = [
    { id: "user", label: "Un utilisateur", icon: "bg-blue-400", desc: "Impact limité" },
    { id: "team", label: "Une équipe", icon: "bg-yellow-400", desc: "Impact modéré" },
    { id: "department", label: "Un département", icon: "bg-orange-500", desc: "Impact important" },
    { id: "company", label: "Toute l'entreprise", icon: "bg-red-500", desc: "Impact critique" },
];

function ImpactOption({ scope }) {
    return (
        <span className="flex items-center gap-2.5">
            <span className={`w-2.5 h-2.5 rounded-full ${scope.icon}`} />
            <span>{scope.label}</span>
            <span className="text-[10px] text-gray-400 ml-auto">{scope.desc}</span>
        </span>
    );
}

export function IncidentForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("high");
    const [affectedSystem, setAffectedSystem] = useState("");
    const [impactScope, setImpactScope] = useState("user");
    const [startedAt, setStartedAt] = useState("");
    const [workaroundAvailable, setWorkaroundAvailable] = useState(false);
    const [workaroundDescription, setWorkaroundDescription] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            title: baseForm.title,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                affected_system: affectedSystem || null,
                impact_scope: impactScope,
                started_at: startedAt || new Date().toISOString(),
                workaround_available: workaroundAvailable,
                workaround_description: workaroundAvailable ? workaroundDescription : null,
            },
        });
    };

    const currentImpact = IMPACT_SCOPES.find((s) => s.id === impactScope);

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Imprimante 2ème étage hors service"
                descriptionPlaceholder="Décrivez l'incident en détail : que se passe-t-il, depuis quand, quel est l'impact..."
            >
                {/* Affected System - OPTIONAL */}
                <div className="form-group">
                    <Label className="form-label">Système / machine / logiciel affecté</Label>
                    <Input
                        value={affectedSystem}
                        onChange={(e) => setAffectedSystem(e.target.value)}
                        placeholder="Ex: Imprimante HP LaserJet, Outlook, Réseau WiFi..."
                        data-testid="affected-system"
                    />
                </div>

                {/* Impact Scope with icons */}
                <div className="form-group">
                    <Label className="form-label">Étendue de l'impact</Label>
                    <Select value={impactScope} onValueChange={setImpactScope}>
                        <SelectTrigger data-testid="impact-scope">
                            <SelectValue>
                                {currentImpact ? <ImpactOption scope={currentImpact} /> : null}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {IMPACT_SCOPES.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                    <ImpactOption scope={s} />
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Started At */}
                <div className="form-group">
                    <Label className="form-label">Début de l'incident</Label>
                    <Input
                        type="datetime-local"
                        value={startedAt}
                        onChange={(e) => setStartedAt(e.target.value)}
                        data-testid="started-at"
                    />
                </div>

                {/* Workaround */}
                <div className="form-group">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="workaround"
                            checked={workaroundAvailable}
                            onCheckedChange={setWorkaroundAvailable}
                            data-testid="workaround-checkbox"
                        />
                        <Label htmlFor="workaround" className="text-sm font-medium cursor-pointer">
                            Un contournement est disponible
                        </Label>
                    </div>
                </div>

                {workaroundAvailable && (
                    <div className="form-group">
                        <Label className="form-label">Description du contournement</Label>
                        <Textarea
                            value={workaroundDescription}
                            onChange={(e) => setWorkaroundDescription(e.target.value)}
                            placeholder="Décrivez le contournement temporaire..."
                            data-testid="workaround-description"
                        />
                    </div>
                )}
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
